import React, {CSSProperties} from 'react'
import styled, {css} from 'styled-components'
import {ThemeColor} from 'themes'
import convertUnit from 'lib/unit'
import {InputRangeProps} from './InputRangeProps'

interface StyledContainerProps {
  descPosition?: 'side' | 'bottom'
}
interface StyledThumbProps {
  thumbColor?: ThemeColor
}

interface StyledSliderProps {
  sliderColor?: ThemeColor
  width: CSSProperties['width']
}

interface StyledBarProp {
  barColor?: ThemeColor
}

interface StyledTickProp {
  tickColor?: ThemeColor
}

const trackStyle = css`
  appearance: none;
  background: transparent;
  border: transparent;
`

const thumbStyle = css`
  pointer-events: all;
  height: ${convertUnit(24)};
  width: ${convertUnit(24)};
  border-radius: 0px;
  border: 0 none;
`

const StyledContainer = styled.div<StyledContainerProps>`
  display: flex;
  flex-direction: ${({descPosition}) =>
    descPosition === 'bottom' ? 'column' : 'row'};
  gap: ${({descPosition}) => (descPosition === 'side' ? convertUnit(20) : 0)};
  justify-content: center;
  align-items: center;
`

const StyledElementContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: ${convertUnit(24)};
  width: 100%;
`

const StyledInputContainer = styled.div`
  width: 100%;
  position: absolute;
  height: ${convertUnit(24)};
`

const StyledInputRange = styled.input`
  position: relative;
  width: 100%;
  appearance: none;
  cursor: pointer;
  height: ${convertUnit(24)};
  opacity: 0;
  z-index: 4;
  padding: 0;

  //track
  &::-moz-range-track {
    ${trackStyle}
  }
  &::-ms-track {
    ${trackStyle}
  }

  &::-webkit-slider-runnable-track {
    ${trackStyle}
  }

  //thumb
  &::-webkit-slider-thumb {
    ${thumbStyle}
  }

  &::-moz-range-thumb {
    ${thumbStyle}
  }

  &::-ms-thumb {
    ${thumbStyle}
  }
`

const StyledCustomSliderContainer = styled.div`
  width: 100%;
  position: absolute;
  height: ${convertUnit(24)};
`

const StyledSlider = styled.div<StyledSliderProps>`
  position: absolute;
  height: 100%;
  width: ${({width}) => width};
  border-top-left-radius: ${convertUnit(50)};
  border-bottom-left-radius: ${convertUnit(50)};
  background-color: ${({theme, sliderColor = 'primary_5'}) =>
    theme[sliderColor]};
`

const StyledThumb = styled.div<StyledThumbProps>`
  width: ${convertUnit(24)};
  height: ${convertUnit(24)};
  border-radius: 50%;
  position: absolute;
  background-color: ${({theme, thumbColor = 'primary_5'}) => theme[thumbColor]};
  top: 50%;
  transform: translate3d(0, -50%, 0);
  z-index: 4;
  cursor: grab;
  right: ${convertUnit(12)};
`

const StyledBar = styled.div<StyledBarProp>`
  position: absolute;
  width: inherit;
  top: 50%;
  transform: translateY(-50%);
  height: 6px;
  border-radius: 3px;
  background-color: ${({theme, barColor = 'info_1'}) => theme[barColor]};
`

const StyledTickContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: absolute;
  justify-content: space-between;
  z-index: 2;
  width: inherit;
`

const StyledTick = styled.div<StyledTickProp>`
  width: ${convertUnit(4)};
  height: ${convertUnit(4)};
  border-radius: 100%;
  background-color: ${({theme, tickColor = 'primary_3'}) => theme[tickColor]};
`

const StyledDescContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: ${convertUnit(12)};
`

export default function InputRange({
  min = 0,
  max = 10,
  step,
  showTick,
  barColor,
  tickColor,
  thumbColor,
  sliderColor,
  leftDesc,
  rightDesc,
  stateValue,
  descPosition = 'bottom',
  onSlide,
  ...props
}: InputRangeProps) {
  const [value, setValue] = stateValue
  const position = ((value - min) / (max - min)) * 100

  return (
    <StyledContainer descPosition={descPosition}>
      {descPosition === 'side' ? leftDesc : <></>}
      <StyledElementContainer>
        <StyledInputContainer>
          <StyledInputRange
            {...props}
            type="range"
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(e) => {
              const val = parseInt(e.currentTarget.value, 10)
              setValue(val)
            }}
            onMouseUp={(e) => {
              const val = parseInt(e.currentTarget.value, 10)
              onSlide && onSlide(val)
            }}
          />
        </StyledInputContainer>

        {showTick ? (
          <StyledTickContainer>
            {Array.from({length: max}, (_, i) => (
              <StyledTick key={i} tickColor={tickColor} />
            ))}
          </StyledTickContainer>
        ) : (
          <></>
        )}

        <StyledCustomSliderContainer>
          <StyledBar barColor={barColor}>
            <StyledSlider
              sliderColor={sliderColor}
              style={{right: `${100 - position}%`}}
              width={`${position}%`}
            />
          </StyledBar>
          <StyledThumb
            thumbColor={thumbColor}
            style={{left: `calc(${position}% - ${convertUnit(12)})`}}
          />
        </StyledCustomSliderContainer>
      </StyledElementContainer>
      {descPosition === 'side' ? rightDesc : <></>}
      {descPosition === 'bottom' ? (
        <StyledDescContainer>
          {leftDesc}
          {rightDesc}
        </StyledDescContainer>
      ) : (
        <></>
      )}
    </StyledContainer>
  )
}
